/**
 * The five screens of the app shell and how they map onto the legacy tabs.
 *
 * The legacy host (`crm/index.html`) still owns one `.panel` per tab and
 * `switchTab(el)` to show it. The shell groups those tabs into five
 * screens; a screen with several tabs shows a view switch in the header:
 *
 *   Today    → today, tasks, activity
 *   People   → contacts, accounts        (People / Companies)
 *   Deals    → pipeline
 *   Inbox    → inbox, outreach            (Outreach only with a program)
 *   Calendar → bookings
 *
 * Settings is not a screen; it opens from the profile menu.
 *
 * URL hashes: the shell writes one canonical hash per view (#people,
 * #companies, #deals, #calendar, …) and still reads every older name
 * (#contacts, #accounts, #pipeline, #bookings, #home, #reports, …) so
 * bookmarks and links from /hunts/onboarding/ keep working.
 */

import type { Product } from '../../lib/product'

export type ScreenId = 'today' | 'people' | 'deals' | 'inbox' | 'calendar'

export type IconKey = 'home' | 'people' | 'deals' | 'inbox' | 'calendar'

export interface ScreenView {
  /** Legacy tab id (`data-tab` on `.nav-tab`, `panel-<tab>`). */
  tab: string
  label: string
  /** Canonical URL hash for this view, without '#'. */
  hash: string
}

export interface Screen {
  id: ScreenId
  label: string
  icon: IconKey
  views: ReadonlyArray<ScreenView>
}

export const SCREENS: ReadonlyArray<Screen> = [
  {
    id: 'today',
    label: 'Today',
    icon: 'home',
    views: [
      { tab: 'today', label: 'Today', hash: 'today' },
      { tab: 'tasks', label: 'Tasks', hash: 'tasks' },
      { tab: 'activity', label: 'Activity', hash: 'activity' },
    ],
  },
  {
    id: 'people',
    label: 'People',
    icon: 'people',
    views: [
      { tab: 'contacts', label: 'People', hash: 'people' },
      { tab: 'accounts', label: 'Companies', hash: 'companies' },
    ],
  },
  {
    id: 'deals',
    label: 'Deals',
    icon: 'deals',
    views: [{ tab: 'pipeline', label: 'Deals', hash: 'deals' }],
  },
  {
    id: 'inbox',
    label: 'Inbox',
    icon: 'inbox',
    views: [
      { tab: 'inbox', label: 'Inbox', hash: 'inbox' },
      { tab: 'outreach', label: 'Outreach', hash: 'outreach' },
    ],
  },
  {
    id: 'calendar',
    label: 'Calendar',
    icon: 'calendar',
    views: [{ tab: 'bookings', label: 'Calendar', hash: 'calendar' }],
  },
]

/** The settings panel is reachable by hash but is not a screen. */
export const SETTINGS_TAB = 'settings'

const VIEW_BY_TAB = new Map<string, ScreenView>()
const SCREEN_BY_TAB = new Map<string, Screen>()
const TAB_BY_HASH = new Map<string, string>()
for (const screen of SCREENS) {
  for (const view of screen.views) {
    VIEW_BY_TAB.set(view.tab, view)
    SCREEN_BY_TAB.set(view.tab, screen)
    TAB_BY_HASH.set(view.hash, view.tab)
    // Every legacy tab id is also accepted as a hash (#contacts, #pipeline…).
    TAB_BY_HASH.set(view.tab, view.tab)
  }
  // Screen ids resolve to the screen's first view (#people, #calendar…).
  const first = screen.views[0]
  if (first) TAB_BY_HASH.set(screen.id, first.tab)
}
TAB_BY_HASH.set(SETTINGS_TAB, SETTINGS_TAB)
// Retired names. Reports was removed; Home was renamed Today.
TAB_BY_HASH.set('home', 'today')
TAB_BY_HASH.set('reports', 'today')
TAB_BY_HASH.set('todo', 'tasks')
TAB_BY_HASH.set('pipeline', 'pipeline')
TAB_BY_HASH.set('bookme', 'bookings')

/** Legacy tab id for a URL hash (with or without '#'), or null if unknown. */
export function tabFromHash(raw: string): string | null {
  const key = raw.replace(/^#/, '').trim().toLowerCase()
  if (!key) return null
  return TAB_BY_HASH.get(key) ?? null
}

/** Canonical hash (without '#') for a legacy tab id. */
export function hashForTab(tab: string): string | null {
  if (tab === SETTINGS_TAB) return SETTINGS_TAB
  return VIEW_BY_TAB.get(tab)?.hash ?? null
}

export function screenForTab(tab: string): Screen | null {
  return SCREEN_BY_TAB.get(tab) ?? null
}

export function isKnownTab(tab: string): boolean {
  return tab === SETTINGS_TAB || VIEW_BY_TAB.has(tab)
}

/** Screens shown in the nav for this product. */
export function visibleScreenIds(
  product: Product,
  showHunts: boolean,
  crmOnly = false,
): ScreenId[] {
  if (crmOnly) return SCREENS.filter((s) => s.id !== 'calendar').map((s) => s.id)
  if (product === 'bookme' && !showHunts) return ['today', 'calendar']
  return SCREENS.map((s) => s.id)
}

/**
 * Legacy tabs reachable in BookMe-only mode (/meet/ with the Hunts screens
 * off): the Today screen's own view, Calendar and Settings. Tasks and
 * Activity live on the Today screen but are Hunts features.
 */
const BOOKME_ONLY_TABS: ReadonlySet<string> = new Set(['today', 'bookings', SETTINGS_TAB])

/** Whether a legacy tab can be shown for this product and Hunts switch. */
export function isTabVisible(
  tab: string,
  product: Product,
  showHunts: boolean,
  crmOnly = false,
): boolean {
  // A CRM-only brand (crm/brand.js) has no Calendar / Book Me.
  if (crmOnly) return tab !== 'bookings'
  if (product === 'bookme' && !showHunts) return BOOKME_ONLY_TABS.has(tab)
  return true
}

/** The tab a product opens on when the URL names none. */
export function defaultTab(product: Product): string {
  return product === 'bookme' ? 'bookings' : 'today'
}

/**
 * Views shown in a screen's view switch. Outreach appears only when the
 * user has an outreach program (or is already looking at it).
 */
export function visibleViews(
  screen: Screen,
  opts: {
    hasOutreach: boolean
    activeTab: string
    product?: Product
    showHunts?: boolean
    crmOnly?: boolean
  },
): ScreenView[] {
  const product = opts.product ?? 'hunts'
  const showHunts = opts.showHunts ?? true
  return screen.views.filter(
    (v) =>
      isTabVisible(v.tab, product, showHunts, opts.crmOnly) &&
      (v.tab !== 'outreach' || opts.hasOutreach || opts.activeTab === 'outreach'),
  )
}
